import {
  ChangeEventHandler,
  FocusEventHandler,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';
import { DraftConfigurationTextStyle } from '@/types';
import { t } from '@/utils/i18n';
import { clamp } from '@/utils/math/clamp';
import { invertAngle } from '@/utils/text/invertAngle';
import { Slider } from '@/components/common/Slider';
import './TextAngle.scss';

const TEXT_ANGLE_MIN = -355;
const TEXT_ANGLE_MAX = 355;

interface TextAngleProps {
  textStyle: DraftConfigurationTextStyle;
  onAngleChange: (angle: number) => void;
  disabled?: boolean;
  hideLabel?: boolean;
}

export const TextAngle: FunctionComponent<TextAngleProps> = ({
  textStyle,
  onAngleChange,
  disabled,
  hideLabel,
}) => {
  const value = invertAngle(textStyle.angle || 0);
  const [sliderValue, setSliderValue] = useState(value);
  const [inputValue, setInputValue] = useState(String(value));

  const changeValue = (newValue: number) => {
    setSliderValue(newValue);
    setInputValue(String(newValue));
  };

  useEffect(() => {
    changeValue(value);
  }, [value, textStyle]);

  const changeInputValue: ChangeEventHandler<HTMLInputElement> = (event) => {
    const eventValue = event.target.value;
    setInputValue(eventValue);

    const newValue = clamp(parseInt(eventValue), TEXT_ANGLE_MAX, TEXT_ANGLE_MIN);
    if (!Number.isNaN(newValue)) {
      setSliderValue(newValue);
    }
  };

  const blurInputValue: FocusEventHandler<HTMLInputElement> = (event) => {
    const newInputValue = clamp(parseInt(event.target.value), TEXT_ANGLE_MAX, TEXT_ANGLE_MIN);
    if (!Number.isNaN(newInputValue) && newInputValue !== value) {
      onAngleChange(newInputValue);
    } else {
      setInputValue(String(value));
    }
  };

  return (
    <div className={cn('text-angle', { 'text-angle--disabled': disabled })}>
      <label className="text-angle__wrapper">
        <span className={cn({ 'sr-only': hideLabel })}>{t('configuration.text.bendingText')}</span>
        <input
          type="number"
          inputMode="numeric"
          className="text-angle__value"
          value={inputValue}
          onBlur={blurInputValue}
          onChange={changeInputValue}
        />
      </label>
      <Slider
        min={TEXT_ANGLE_MIN}
        max={TEXT_ANGLE_MAX}
        initialValue={sliderValue}
        onSave={onAngleChange}
        onChange={changeValue}
      />
    </div>
  );
};
